import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { money, shortDate } from '@/lib/format'
import { presentation } from '@/lib/quotationStatus'
import { useAsync } from '@/lib/useAsync'
import { PageHeader } from '@/components/ui/page'
import { Badge, Busy, Button, Card, EmptyState, ErrorState } from '@/components/ui/primitives'
import { CustomerPicker } from '@/components/CustomerPicker'
import type { ComboboxOption } from '@/components/ui/combobox'
import { Dialog } from '@/components/ui/dialog'
import { Field, Input, Select } from '@/components/ui/form'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  Paged,
  QuotationListItem,
  QuotationPreparer,
  QuotationStatus,
  ResellerListItem,
} from '@/types/api'

/**
 * Two entries here are not statuses, and both earn their place for the same reason: to a
 * rep this is one control answering one question - what am I looking at - and splitting
 * that across a status list and two checkboxes elsewhere would be splitting the question.
 *
 * <p>
 * "Deleted" is a mark rather than a status; the status underneath it is whatever it was.
 * "Open" is a set: everything nobody has finished with, which is the answer a rep wants
 * most often and previously took five passes through this menu to assemble, and is what
 * the listing now opens on. The set is the server's - `QuotationLifecycle.InFlightStatuses`
 * - so the browser names it and never defines it.
 * </p>
 */
type StatusFilter = QuotationStatus | 'Deleted' | 'Open' | ''

/**
 * The menu, in the order a quotation lives.
 *
 * <p>
 * Grouped rather than flat. Every status has a row, which is thirteen entries, and a flat
 * thirteen is a list somebody reads twice - the grouping is what lets a person find the
 * half they are in before reading any label. "Finished" rather than "Closed": the API's
 * `IsClosed` means won or lost, and Cancelled is deliberately in neither denominator, so
 * borrowing that word here would make the menu disagree with the figures.
 * </p>
 */
const IN_FLIGHT = [
  { value: 'Draft', label: 'Draft' },
  { value: 'PendingApproval', label: 'Pending approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Changes needed' },
  { value: 'Sent', label: 'Sent' },

  // The customer has signed and nobody has recorded the win yet. It had no row at all,
  // which made the one state that always needs chasing the one nobody could filter to.
  { value: 'Accepted', label: 'Accepted' },
] as const satisfies readonly { value: QuotationStatus; label: string }[]

const FINISHED = [
  { value: 'Won', label: 'Won' },
  { value: 'Lost', label: 'Lost' },

  // A customer who answered no, as against one who went quiet - which is Expired.
  { value: 'Declined', label: 'Declined' },
  { value: 'Expired', label: 'Expired' },

  // Called off with a stated reason. Neither won nor lost, and it stays out of both
  // denominators, which is exactly why somebody needs to be able to list them.
  { value: 'Cancelled', label: 'Cancelled' },
] as const satisfies readonly { value: QuotationStatus; label: string }[]

type ListedStatus = (typeof IN_FLIGHT)[number]['value'] | (typeof FINISHED)[number]['value']

type AssertNever<T extends never> = T

/**
 * Compile-time proof that every status has a row.
 *
 * <p>
 * The server has a test that says the same thing about its own sets, and this is the half
 * of it the browser owns: a status added to the union with no row here stops the build
 * rather than becoming a state a rep can reach through Every status and through nothing
 * else. That is how Accepted, Declined and Cancelled went missing in the first place.
 * </p>
 */
export type EveryStatusHasARow = AssertNever<Exclude<QuotationStatus, ListedStatus>>

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = '-issued'

/**
 * What the listing opens on.
 *
 * <p>
 * "Open" rather than every status, because most of what this listing holds is finished
 * with, and a first page of closed deals is one a rep reads past to reach the work they
 * came for. The set is already the answer they were selecting by hand nearly every time.
 * Every status is one entry away and stays the answer for looking a closed deal up; the
 * empty state says which listing is empty, so a country with nothing open never reads as
 * a country with nothing.
 * </p>
 */
const DEFAULT_STATUS: StatusFilter = 'Open'

export function Quotations() {
  const { can, scope, user } = useAuth()
  const { current } = useCountry()
  const navigate = useNavigate()
  const canSeeOthers = scope('quotation.view') !== 'own'
  const [status, setStatus] = useState<StatusFilter>(DEFAULT_STATUS)
  const deleted = status === 'Deleted'
  const open = status === 'Open'
  const [search, setSearch] = useState('')

  /**
   * '' is everyone the caller can see, 'mine' is the caller, anything else is one
   * person's id. One control rather than a checkbox beside a picker, because it answers
   * one question - whose work am I looking at - and every answer to it is exclusive.
   */
  const [preparedBy, setPreparedBy] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [creating, setCreating] = useState(false)

  const list = useAsync<Paged<QuotationListItem>>(
    (signal) =>
      api.get<Paged<QuotationListItem>>(
        '/quotations',
        {
          countryId: current?.countryId,

          // Neither of the two set-valued entries is a status, so neither is sent as one.
          status: deleted || open ? '' : status,
          deleted,
          open,
          search,
          mineOnly: preparedBy === 'mine',
          preparedByUserId: preparedBy === '' || preparedBy === 'mine' ? undefined : preparedBy,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [current?.countryId, status, deleted, open, search, preparedBy, sort, page, pageSize],
  )

  /**
   * Everybody who has raised something this caller can read, from the same scoped query
   * the listing runs - so the menu can never name somebody whose quotations it would then
   * withhold. It follows the country and the deleted view for the same reason: a name
   * offered against a listing it returns nothing in is a name that reads as a bug.
   */
  const preparers = useAsync<QuotationPreparer[]>(
    (signal) =>
      canSeeOthers
        ? api.get<QuotationPreparer[]>(
            '/quotations/preparers',
            { countryId: current?.countryId, deleted },
            signal,
          )
        : Promise.resolve([]),
    [canSeeOthers, current?.countryId, deleted],
  )

  // Switching country, or into the deleted view, can take the chosen person out of the
  // menu - and a select whose value matches no option renders blank while still filtering
  // by it, which reads as a listing that has broken. Falling back to everyone is the
  // honest answer: this caller can no longer ask that question here.
  useEffect(() => {
    if (preparedBy === '' || preparedBy === 'mine' || !preparers.data) return
    if (preparers.data.some((person) => person.userId === preparedBy)) return

    setPreparedBy('')
    setPage(1)
  }, [preparers.data, preparedBy])

  function refine(apply: () => void) {
    apply()
    setPage(1)
  }

  return (
    <>
      <PageHeader
        eyebrow={current?.name}
        title="Quotations"
        hint="Every quotation routes through an approval chain, whatever it is worth."
        actions={
          can('quotation.create') && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New quotation
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: '-issued', label: 'Newest issued' },
          { value: 'issued', label: 'Oldest issued' },
          { value: '-value', label: 'Highest value' },
          { value: 'value', label: 'Lowest value' },
          { value: 'customer', label: 'Customer A–Z' },
          { value: 'status', label: 'Status' },
          { value: 'validuntil', label: 'Expiring soonest' },
          { value: 'number', label: 'Quotation number' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search number or customer"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect
          label="Status"
          value={status}
          width="w-48"
          onChange={(next) => refine(() => setStatus(next as StatusFilter))}
        >
          <option value="">Every status</option>

          {/* Above the groups, because it is a shortcut past them rather than one of
              them: everything in flight, in one entry. */}
          <option value="Open">Open</option>

          <optgroup label="In flight">
            {IN_FLIGHT.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </optgroup>

          <optgroup label="Finished">
            {FINISHED.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </optgroup>

          {/* Its own line at the bottom, in neither group: it is a mark rather than a
              status, and the status underneath it is whatever it was. */}
          <option value="Deleted">Deleted</option>
        </FilterSelect>

        {canSeeOthers && (
          <FilterSelect
            label="Prepared by"
            value={preparedBy}
            width="w-52"
            onChange={(next) => refine(() => setPreparedBy(next))}
          >
            <option value="">Everyone I can see</option>
            <option value="mine">Only mine</option>

            {/* Named people below the two set answers, and the caller is not among them -
                "Only mine" is already that answer, and offering it twice under two labels
                makes the menu look like the two do different things. */}
            {(preparers.data ?? []).filter((person) => person.userId !== user?.id).length > 0 && (
              <optgroup label="One person">
                {(preparers.data ?? [])
                  .filter((person) => person.userId !== user?.id)
                  .map((person) => (
                    <option key={person.userId} value={person.userId}>
                      {person.displayName}
                    </option>
                  ))}
              </optgroup>
            )}
          </FilterSelect>
        )}
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState
                title={deleted ? 'Nothing deleted' : open ? 'Nothing open' : 'No quotations here'}
                hint={
                  deleted
                    ? 'Quotations taken out of the listing appear here, and can be put back.'
                    : open
                      ? 'Nothing is in a draft, waiting on an approver, or sitting with a customer.'
                      : 'A quotation is built from solution blocks: a product, its narrative and its pricing ladder.'
                }
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="number" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Number
                    </SortableTh>
                    <SortableTh field="customer" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Customer
                    </SortableTh>
                    <Th>Prepared by</Th>
                    <SortableTh field="value" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))} numeric>
                      Value
                    </SortableTh>
                    <SortableTh field="issued" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Issued
                    </SortableTh>
                    <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((quotation) => (
                    <Tr key={quotation.id} onClick={() => navigate(`/quotations/${quotation.id}`)}>
                      <Td>
                        <span className="tabular text-[13px]">
                          {/* A draft has no number yet: it gets one at submit, so abandoned
                              drafts do not burn numbers out of the country's sequence. */}
                          {quotation.quotationNo ?? <span className="text-faint">Not numbered</span>}
                        </span>
                        {quotation.revisionNo > 1 && (
                          <span className="ml-2 font-mono text-[11px] text-muted">
                            rev {quotation.revisionNo}
                          </span>
                        )}
                      </Td>
                      <Td>
                        <span className="font-medium">{quotation.customerName}</span>
                        {quotation.resellerName && (
                          <span className="block text-[12px] text-muted">via {quotation.resellerName}</span>
                        )}
                      </Td>
                      <Td className="text-muted">{quotation.preparedByName}</Td>
                      <Td numeric>{money(quotation.grandTotal, quotation.currencySymbol)}</Td>
                      <Td className="text-muted">{shortDate(quotation.issueDate)}</Td>
                      <Td>
                        <span className="flex items-center gap-1.5">
                          {/* On the deleted listing the badge says deleted first, because
                              that is why the row is on this screen. A draft's own status
                              adds nothing beside it; a won deal's does - a deleted deal is
                              still a deal that was signed, and hiding that here would make
                              the two kinds of deleted row look like one. */}
                          {quotation.deletedAt && <Badge tone="critical">Deleted</Badge>}
                          {(!quotation.deletedAt || quotation.status !== 'Draft') && (
                            <Badge tone={presentation(quotation.status).tone}>
                              {presentation(quotation.status).label}
                            </Badge>
                          )}
                          {quotation.hasTermsOverride && <Badge tone="warning">Amended terms</Badge>}
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <NewQuotation
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => navigate(`/quotations/${id}`)}
      />
    </>
  )
}

export function NewQuotation({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const { current } = useCountry()
  const toast = useToast()
  const countryId = current?.countryId
  const [customer, setCustomer] = useState<ComboboxOption | null>(null)
  const [resellerId, setResellerId] = useState('')
  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)

  // A choice made under one country is not a choice under the next. Cancelling keeps what
  // was entered, so without this the dialog reopens after a switch still holding another
  // country's customer and reseller - the first on show, the second behind a select that
  // no longer has an option for it and so reads as "Direct".
  useEffect(() => {
    setCustomer(null)
    setResellerId('')
  }, [countryId])

  const resellers = useAsync<Paged<ResellerListItem>>(
    (signal) =>
      open
        ? api.get<Paged<ResellerListItem>>(
            '/resellers',
            { countryId: current?.countryId, pageSize: 200 },
            signal,
          )
        : Promise.resolve({ items: [], page: 1, pageSize: 0, totalCount: 0, totalPages: 0, hasMore: false }),
    [open, current?.countryId],
  )

  async function create() {
    if (!current || !customer) return

    setSaving(true)
    try {
      const created = await api.post<{ id: string }>('/quotations', {
        countryId: current.countryId,
        customerId: customer.value,
        resellerId: resellerId || null,
        title: title || null,
      })

      onCreated(created.id)
      setCustomer(null)
      setResellerId('')
      setTitle('')
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not start the quotation', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New quotation"
      description={`Raised in ${current?.name ?? 'the current country'}, for a customer in it.`}
      onClose={onClose}
      loading={resellers.loading}
      error={resellers.error}
      onRetry={resellers.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" loading={saving} disabled={!customer} onClick={() => void create()}>
            Start building
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field
          label="Customer"
          required
          hint="Only customers in this country. Quoting one country's customer on another's letterhead is the mistake this prevents."
        >
          {(id) => (
            <CustomerPicker
              id={id}
              countryId={countryId}
              countryName={current?.name}
              value={customer}
              onChange={setCustomer}
            />
          )}
        </Field>

        <Field
          label="Reseller"
          hint="Who the deal came through. Leave empty for a direct sale."
        >
          {(id) => (
            <Select id={id} value={resellerId} onChange={(event) => setResellerId(event.target.value)}>
              <option value="">Direct - no reseller</option>
              {resellers.data?.items.map((reseller) => (
                <option key={reseller.id} value={reseller.id}>
                  {reseller.name}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Title" hint="What the proposal is called on its cover. Editable later.">
          {(id) => <Input id={id} value={title} onChange={(event) => setTitle(event.target.value)} />}
        </Field>
      </div>
    </Dialog>
  )
}
